<script setup lang="ts">
/**
 * The garage floor: your tank, standing in it, and the button that takes it out.
 *
 * This is the screen the player lands on, and its job is different from every
 * other tab's. The hulls and turrets tabs are a *shop* — grids of things to
 * compare and buy. This is where you keep what you already own, so it shows one
 * tank large, the alternates parked along the back wall, and the way out in the
 * middle. Nothing here sells anything.
 *
 * ## The scene is the screen, not a panel on it
 *
 * The hangar fills the tab edge to edge and **everything else floats on top of
 * it**. It began as a boxed picture in a column beside a stats panel, which is
 * the layout a document wants and the opposite of the one a place wants: a
 * bordered rectangle of room with grey either side is a photograph in a frame,
 * however good the render inside it is. A player is supposed to be standing in
 * the garage, and you cannot stand inside something that has a margin.
 *
 * Everything else follows from that inversion:
 *
 *   **Nothing sits beside the tank, so nothing steals its width.** The quest
 *   card, the numbers, the bays and the button are corners of the same picture.
 *   The middle is deliberately clear of controls, which is also what keeps the
 *   orbit usable — the tank ends up the biggest pointer target on the tab.
 *
 *   **Every panel has to survive being over a photograph.** Translucent and
 *   blurred rather than opaque, because an opaque slab is the old boxed layout
 *   with the box moved; but *dark* and blurred rather than merely translucent,
 *   because the backdrop's brightest region moves as the camera orbits and text
 *   that is legible over the near wall is not legible over a window.
 *
 *   **The bays are small on purpose.** They are a bookmark bar, not a
 *   catalogue: four tanks already owned, one click each. At card size they
 *   competed with the vehicle for the eye, on the one tab whose entire subject
 *   is the vehicle.
 *
 *   **And the bar does not move.** Four boxes, always, with the tank you are
 *   driving marked in place rather than lifted out of the row. See `bays`.
 */
import { computed, watch } from 'vue';

import DailyQuestBox from '@/garage/DailyQuestBox.vue';
import ItemPreview from '@/garage/ItemPreview.vue';
import TankPanel from '@/garage/TankPanel.vue';
import { groupedSpecStats, specValues as resolveSpecValues } from '@/garage/stat-display';
import { useGarage } from '@/state/garage';
import { useFavourites, type Favourite } from '@/state/favourites';
import { showNotice } from '@/state/notification-banner';

const props = defineProps<{
  /** Which player these favourites belong to. Slots are stored per account. */
  playerKey: string;
  /*
   * There is deliberately no `mapId`, `mode` or `mission` here.
   *
   * They existed for one thing — the mode/map sub-header that used to sit in the
   * top-left corner — and that readout is gone, so the choice of battlefield is
   * now stated in exactly one place: the battle picker, whose footer summary
   * (`battle-picker-summary`) names the mode and the map beside the button that
   * acts on them. Passing them here again would put a second statement of one
   * fact on screen, which is the argument this file makes about the convoy's
   * health and about kills on `battleOver`, one screen along.
   */
  /**
   * Why the picker's Deploy will refuse, or null when it will not.
   *
   * Distinct from `canDeploy`, which is fuel — a screen that blamed an empty tank
   * for a room the server could not open would send the player off to buy fuel
   * that would not help. Neither disables the In-battle button; both change what
   * it says it will cost. The refusal itself lives on Deploy, in the dialog.
   */
  blockedReason?: string | null;
  canDeploy: boolean;
  /** Fuel a sortie costs, shown on the button so it is not a surprise. */
  fuelCost: number | null;
  /**
   * A phone held sideways — `GarageScreen`'s `COMPACT_QUERY`, resolved there and
   * handed down.
   *
   * Not re-asked with a second `matchMedia`, for the reason `showSidebar` is
   * resolved once one file up: two independent readings of one viewport question
   * disagree for a frame after every rotation, and this one drives both a class
   * and a `v-if`. See the compact block at the foot of this file for what it does.
   */
  compact?: boolean;
}>();

const emit = defineEmits<{
  /**
   * Open the battle picker.
   *
   * The only way out of the garage now — the picker holds the Deploy button, so
   * this screen chooses a tank and hands over, and there is no second control
   * here that could start a sortie the dialog was not describing.
   */
  changeBattle: [];
  customise: [hullId: string];
  /** Opens the daily-quest window. The screen owns it, exactly as it owns the rank ladder. */
  quests: [];
  /** The small re-roll button on the quest card. */
  rerollQuests: [];
  /**
   * The daily set rolled over while the garage was open.
   *
   * Raised, never acted on: re-reading the overview belongs to whoever owns the
   * store, and the card must not decide for itself that a new day has begun —
   * whether it has is the server's answer. The same seam `DailyQuestsWindow`
   * uses, which is why both can be open at once without either inventing a day.
   */
  questsElapsed: [];
}>();

const garage = useGarage();
const favourites = useFavourites();

const equipped = computed<Favourite | null>(() => {
  const hullId = garage.equippedHull.value?.id;
  const turretId = garage.equippedTurret.value?.id;
  return hullId && turretId ? { hullId, turretId } : null;
});

/**
 * Watched rather than called once on mount, because seeding the first bay needs
 * to know what is equipped and the lobby can render before the garage has
 * answered. `load` claims the player only when it has something to claim them
 * with, so the extra calls are free and the first useful one wins.
 */
watch(equipped, (value) => favourites.load(props.playerKey, value), { immediate: true });

const currentHull = computed(() => garage.equippedHull.value);
const currentTurret = computed(() => garage.equippedTurret.value);

/**
 * Which bay the player is driving — derived from the equipped tank, never
 * stored. -1 means they have fitted something that has no bay yet, which is a
 * real state: it is what makes an empty bay worth clicking.
 */
const selectedBay = computed(() => favourites.indexOf(equipped.value));

/**
 * Every bay, in the order they were parked, including the one being driven.
 *
 * Hiding the current bay was the original design and was the bug: driving a
 * saved tank removed its box from the row and put the previous tank's back, so
 * every click shuffled the strip and the tanks appeared to swap places under the
 * pointer — reported as *"tanks just fly in and out of the list"*. The count of
 * boxes changed too, since the free-bay row is `MAX − parked`, so the whole
 * strip re-laid itself around a click that was supposed to change one thing.
 *
 * There are now always exactly `MAX_FAVOURITES` boxes and nothing ever moves
 * between them. Which one you are driving is shown, not enacted — the same rule
 * `favourites.ts` follows for the selection itself.
 */
const bays = computed(() => favourites.slots.value.map((slot, index) => ({ slot, index })));

const specValues = computed(() => resolveSpecValues(garage.spec.value));

/** Firepower / Survivability / Mobility. See `StatGroup` for why not hull/turret. */
const statGroups = groupedSpecStats();

/**
 * The quest card's data, straight off the overview.
 *
 * Passed through whole rather than picked apart here: which quest is featured,
 * how full each segment is and what the clock reads are `daily-quest-box.ts`'s
 * answers, and the card is what draws them. This screen decides only *whether*
 * there is a card — a box that named a quest before the server had dealt one
 * would be advertising something that does not exist.
 *
 * `loadedAtMs` is read from the store rather than taken as a prop, because it is
 * a fact about the response the figures came in, not a decision this screen
 * makes. It is what the countdown is a subtraction against.
 */
const quests = computed(() => garage.quests.value);
const hasQuests = computed(() => quests.value.quests.length > 0);

/** Names a saved pair from the catalogue, so a slot is not two opaque ids. */
function nameOf(slot: Favourite): { hull: string; turret: string } {
  const hull = garage.hulls.value.find((h) => h.id === slot.hullId);
  const turret = garage.turrets.value.find((t) => t.id === slot.turretId);
  return { hull: hull?.name ?? 'Unknown hull', turret: turret?.name ?? 'Unknown turret' };
}

/**
 * Drives a saved tank.
 *
 * Two `equip` calls' worth of intent in one, and it goes through the garage
 * store like every other mutation — the server decides whether the player owns
 * these, exactly as it does on the hulls tab. A slot naming something no longer
 * owned therefore fails loudly rather than deploying a tank nobody bought.
 *
 * **Deliberately silent on success.** `driveBay` is the store's quiet pair, and
 * it exists because this control fired two ordinary equips and got two ordinary
 * banners for them — `Hull fitted`, then `Turret fitted` — every single time
 * anybody switched tanks. The bay lighting up is the confirmation, and it is the
 * thing the player is already looking at. A *refusal* is not silent: it lands in
 * the store's `error`, which `GarageScreen` draws as a strip directly above this
 * floor, carrying the server's own wording — which is more use than the fixed
 * sentence that used to be here, and which, because `mutate` never rethrows,
 * this function's `catch` had in fact stopped reaching a long time ago.
 */
async function drive(index: number): Promise<void> {
  const slot = favourites.slots.value[index];
  if (!slot || index === selectedBay.value) return;
  await garage.driveBay(slot.hullId, slot.turretId);
}

/**
 * Clears a bay.
 *
 * Nothing here has to fix up the selection, because there is none to fix: which
 * bay is being driven is derived from the equipped tank. Clearing the one you
 * are in simply means you are now driving something unsaved — a real state, and
 * the one that makes an empty bay worth clicking.
 */
function removeBay(index: number): void {
  const slot = favourites.slots.value[index];
  if (!slot) return;
  favourites.remove(index);
  showNotice({ title: `${nameOf(slot).hull} cleared from the garage.`, lines: [] });
}

/**
 * Whether the tank on screen already has a bay.
 *
 * Without this the three empty bays all read "Park this tank" and all refuse,
 * because the tank being shown *is* bay 0 — a row of buttons whose only
 * behaviour is to say no. Saying what would make them work is better than
 * letting the player find out by clicking.
 */
const alreadyParked = computed(() => selectedBay.value >= 0);

function saveCurrent(): void {
  const current = equipped.value;
  if (!current) return;
  const result = favourites.save(current);
  if (result === 'duplicate') {
    showNotice({ title: 'That tank is already in your garage.', lines: [], kind: 'error' });
  } else if (result === 'full') {
    showNotice({ title: 'All four bays are full — clear one first.', lines: [], kind: 'error' });
  } else {
    showNotice({ title: 'Parked in a free bay.', lines: [] });
  }
}
</script>

<template>
  <div class="lobby" :class="{ compact: props.compact }" data-test="lobby">

    <aside v-if="hasQuests" class="quests">
      <DailyQuestBox
        :quests="quests"
        :loaded-at-ms="garage.loadedAtMs.value"
        :diamonds="garage.diamonds.value"
        @open="emit('quests')"
        @reroll="emit('rerollQuests')"
        @elapsed="emit('questsElapsed')"
      />
    </aside>

    <section class="floor">

      <p v-if="props.blockedReason" class="battle-blocked" data-test="deploy-blocked">
        {{ props.blockedReason }}
      </p>

      <TankPanel
        :hull-name="currentHull?.name ?? null"
        :turret-name="currentTurret?.name ?? null"
        :stat-groups="statGroups"
        :values="specValues"
        :compact="props.compact"
        @customise="(id) => emit('customise', id)"
      />
    </section>

    <footer class="dock">
      <div class="bays" data-test="lobby-bays">
        <div
          v-for="entry in bays"
          :key="`${entry.slot.hullId}|${entry.slot.turretId}`"
          class="bay"
          :class="{ current: entry.index === selectedBay }"
        >
          <button
            type="button"
            class="bay-pick"
            :data-test="`lobby-bay-${entry.index}`"
            :disabled="garage.busyId.value !== null || entry.index === selectedBay"
            :aria-current="entry.index === selectedBay ? 'true' : undefined"
            :title="
              entry.index === selectedBay
                ? `You are driving this — ${nameOf(entry.slot).hull} · ${nameOf(entry.slot).turret}`
                : `${nameOf(entry.slot).hull} · ${nameOf(entry.slot).turret}`
            "
            @click="drive(entry.index)"
          >
            <span class="bay-shot">
              <ItemPreview
                :hull-id="entry.slot.hullId"
                :turret-id="entry.slot.turretId"
                framing="tank"
                :label="nameOf(entry.slot).hull"
              />
            </span>
            <span class="bay-name">{{ nameOf(entry.slot).hull }}</span>
          </button>

          <button
            v-if="!props.compact"
            type="button"
            class="bay-remove"
            :data-test="`lobby-bay-remove-${entry.index}`"
            :aria-label="`Remove ${nameOf(entry.slot).hull} from the garage`"
            :title="`Remove ${nameOf(entry.slot).hull} from the garage`"
            @click="removeBay(entry.index)"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <button
          v-for="n in favourites.freeSlots.value"
          :key="`free-${n}`"
          type="button"
          class="bay empty"
          :data-test="`lobby-bay-free-${n}`"
          :disabled="!equipped || alreadyParked"
          :title="
            alreadyParked
              ? 'Fit another tank on the hulls or turrets tab, then park it here'
              : 'Save the current loadout to a bay'
          "
          @click="saveCurrent()"
        >
          <span class="plus" aria-hidden="true">+</span>
          <span class="bay-name">{{ alreadyParked ? 'Empty bay' : 'Park this' }}</span>
        </button>
      </div>

      <button
        type="button"
        class="battle"
        data-test="battle-picker-open"
        @click="emit('changeBattle')"
      >
        In battle
        <em v-if="props.blockedReason">· unavailable</em>
        <em v-else-if="!props.canDeploy">· out of fuel</em>
        <em v-else-if="props.fuelCost !== null">· {{ props.fuelCost }} fuel</em>
      </button>
    </footer>
  </div>
</template>

<style scoped>
/*
 * Four corners over one scene.
 *
 * A grid rather than four absolutely positioned boxes: absolute positioning
 * would let the readout and the battle line overlap when one of them grows —
 * and the readout grows whenever a stat gains a row — whereas grid tracks push
 * against each other and the middle row absorbs it. The middle row is `1fr` and
 * holds nothing, which is the point: it is the tank's space, and it is the last
 * thing to be given up.
 */
/*
 * **Two columns now, not three, and the second one is the panel.**
 *
 * The readout used to hold column 1 and the battle line column 3, with the tank
 * between them. Everything numeric is on the right in one panel now.
 *
 * **The dock spans both columns, because the tank is centred in the window.**
 * It used to span column 1 alone, which centred the bays and the In-battle
 * button under a vehicle that `TANK_SCREEN_SHIFT` was pushing left of centre by
 * half the panel's width — the two centres agreed because both were the left
 * column's. The shift is 0 now (see `hangar-scene.ts`), so the tank stands on
 * the window's own centre line and the dock has to as well. **These two move
 * together or they do not move at all**: a dock centred on one column under a
 * tank centred on the window is a strip visibly off the thing it belongs to,
 * which is the arrangement this replaced.
 */
.lobby {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: auto minmax(0, 1fr) auto;
  /* Panels sit at the top of their track rather than filling it. Without this
     the battle line stretches to whatever height the readout gave row 1 — a
     three-word label in a box six hundred pixels tall, which is how it first
     rendered. */
  align-items: start;
  gap: 0.9rem;
  /*
   * Top padding clears the title bar, which is a scrim over this scene rather
   * than a bar above it — see `.garage.immersive` in `GarageScreen.vue`. The bar
   * reserves no space, so the first thing under it has to leave some.
   *
   * `--bar-h` is that bar's **measured** height, published by `GarageScreen` from
   * a `ResizeObserver`, plus a gap. It replaced a flat `3.6rem`, which was 8 px
   * short of the bar at every width and 96 px short below 900 px, where the strip
   * wraps onto three rows — survivable while a one-line readout held this corner
   * and not survivable now the daily-quest card does, the bar being `z-index: 3`
   * against this tab's 1. The fallback is the old figure, for the frame before
   * the first measurement and for anything without `ResizeObserver`.
   */
  padding: calc(var(--bar-h, 3.6rem) + 0.55rem) 0.9rem 0.9rem;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  /*
   * The room's canvas is a *sibling* of this whole tab now — `GarageScreen`
   * mounts it once, behind every tab — so the lobby itself must be glass:
   * an element with default pointer-events receives hits anywhere in its
   * box, background or not, and this grid covers the window. The regions
   * below already run the containers-none/controls-auto pattern, and an
   * explicit `auto` on a control overrides this inherited `none`.
   */
  pointer-events: none;
}

/*
 * Everything else sits above the scene and, crucially, only where it is drawn.
 * `pointer-events: none` on the containers with it restored on the controls is
 * what keeps the drag-to-orbit working across the gaps between panels — without
 * it the top-left quadrant is a dead zone shaped like a grid cell.
 */
.quests,
.floor,
.dock {
  position: relative;
  z-index: 1;
  pointer-events: none;
}

/*
 * Controls take the pointer back, region by region, so the room between the
 * panels keeps drag-to-orbit. The quest card is a control — the whole card is one
 * button — which is why `.quests` is in this list and the readout it replaced was
 * deliberately not.
 */
.quests > *,
.floor > *,
.dock > * {
  pointer-events: auto;
}

/*
 * Top-left, in the corner the mode/map readout used to hold.
 *
 * Placement only: the card carries its own panel treatment, and `justify-self:
 * start` lets it be its own width rather than a column's — the track is
 * `minmax(0, 1fr)` and a stretched card would run most of the way to the tank.
 *
 * Row 1 is `auto`, so this corner is now as tall as the card rather than as tall
 * as a one-line header. Nothing is displaced by that: the middle row is the `1fr`
 * that absorbs it, it holds no controls by design, and the room itself is a
 * sibling canvas `GarageScreen` mounts behind every tab — so a taller corner
 * cannot resize the scene the way it could when the hangar lived in here.
 */
.quests {
  grid-column: 1;
  grid-row: 1;
  justify-self: start;
}

/*
 * Panels over a photograph.
 *
 * Dark, translucent and blurred, in that order of importance. The blur is not
 * decoration — the backdrop has hard bright window frames in it and small text
 * laid straight over one is illegible, while a blur turns whatever is behind
 * into a tone the text can sit on regardless of where the camera is pointing.
 * The fallback for a browser without `backdrop-filter` is simply a slightly
 * transparent dark panel, which is fine.
 */
.panel {
  padding: 0.7rem 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 4px;
  background: rgba(10, 13, 17, 0.62);
  backdrop-filter: blur(9px);
}

.panel h2 {
  margin: 0;
  font-size: 1.05rem;
}

.panel h3 {
  margin: 0 0 0.35rem;
  color: var(--steel-400);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.identity {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.part {
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
}

.part-label {
  font-size: 0.6rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--steel-400);
}

.part-name {
  font-size: 1.05rem;
  font-weight: 600;
}

/* The group heading is quieter than the panel heading above it — three levels
   of shouting in one narrow column is unreadable. */
.stat-group + .stat-group {
  margin-top: 0.7rem;
}

.stat-group h4 {
  margin: 0 0 0.25rem;
  padding-bottom: 0.2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  color: var(--accent);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hint {
  margin: 0 0 0.5rem;
  color: var(--steel-400);
  font-size: 0.72rem;
  line-height: 1.4;
}

/*
 * A column, not a row: the refusal note stacks above the tank panel.
 *
 * `align-items: flex-end` keeps both children hard against the right edge and
 * lets each be its own width — the note is a sentence rather than a panel, and
 * stretching it to the panel's width would make it look like one. Neither takes a
 * grid track of its own, so the scene behind is untouched however long a refusal
 * gets. The quest card used to head this column and holds the top-left corner
 * now; `align-items` stayed because the note still wants it.
 */
/*
 * The right-hand column: the quest card, then the tank panel under it.
 *
 * **Spans all three rows, so the panel gets the whole height of its column.**
 * It used to stop at row 2 — above the dock — which was right while the dock held
 * column 1 alone: the two were side by side and neither could reach the other.
 * The dock spans both columns now (the tank is centred, so the strip under it has
 * to be), and its 157 px box therefore sits under the panel as well: measured at
 * 1280 × 800, stopping at row 2 left the panel 373 px tall with five of eleven
 * stats visible and 200 px of empty column beneath it.
 *
 * That is an overlap rather than a collision **only while the window is wide
 * enough**. The dock's cluster is a fixed ~444 px of bays plus the button,
 * centred, so its right edge is at `W/2 + 222` and the panel's left edge is at
 * `W - 398`: they meet at 1240 px, so the guard is 78 rem (1248 px) rather than
 * the 1240 itself — a break *at* the meeting point leaves a few pixels of touch
 * on either side of it. Below that the media query at the foot of this
 * file puts the column back to rows 1–2, which is the arrangement measured safe —
 * a shorter panel whose stats scroll, rather than bays drawn over a stat list.
 */
/*
 * `align-self: stretch` overrides the grid's own `align-items: start`, and it is
 * what makes the panel's `max-height: 100%` mean anything: unstretched, this
 * column sizes to its content, so the panel's ceiling resolved against its own
 * height and the stat list never scrolled — at 900 px it ran 17 px past the
 * bottom of the window and the Customize button sat on the edge of the screen.
 * The grid keeps `align-items: start` for the reason stated there (the battle
 * line must not stretch to whatever height row 1 was given); this one item is
 * the exception, because it is the only one that wants the height.
 */
.floor {
  grid-column: 2;
  grid-row: 1 / -1;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  min-width: 0;
  min-height: 0;
}

/* The refusal, in the same translucent treatment as every other panel floating
   over the scene. Narrower than the card on purpose: it is a sentence. */
.battle-blocked {
  max-width: 22rem;
  margin: 0.4rem 0 0;
  padding: 0.45rem 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-left: 2px solid var(--accent, #e8722c);
  border-radius: 4px;
  background: rgba(10, 13, 17, 0.62);
  backdrop-filter: blur(9px);
  font-size: 0.72rem;
  line-height: 1.4;
  color: var(--steel-400);
}

/* The bottom strip: bays hard left, the way out hard right. */
/*
 * **Centred, not split to the corners.**
 *
 * The bays and the way out are both things you do *with the tank you are looking
 * at*, and pushing them to opposite ends of a 1440 px window put two related
 * controls a screen apart with the vehicle's own reflection between them. Centred
 * they read as one strip under the tank, which is also where the eye already is.
 *
 * Two consequences:
 *
 * - **This row may hold exactly two children.** At `space-between` a third child
 *   landed in the middle and that was survivable; centred, anything between the
 *   bays and the In-battle button splits the cluster in half. A confirmation —
 *   parked, cleared, a bay that could not be fitted — goes through the shared
 *   `ui/NotificationBanner.vue` now rather than a strip lifted above this row, so
 *   a future third control belongs inside `.bays` or `.launch` rather than
 *   beside them.
 * - **It wraps rather than overflowing.** The cluster is about 45 rem wide, so it
 *   fits any ordinary window and would spill off a very narrow one; wrapping
 *   costs a row of the scene at that width, which is the better of the two
 *   failures — the rule this layout is built around is that nothing takes a row
 *   *while the window is a normal size*, not that the strip may run off the edge.
 */
.dock {
  grid-column: 1 / -1;
  grid-row: 3;
  display: flex;
  /* Stacked, not side by side: the strip belongs *above* the way out, as the
     reference has it, and side by side made the button a neighbour of the bays
     rather than the conclusion of them. */
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  /* Clear of the music control, which is pinned to the bottom centre of the
     window by a different component. Overlapping it put a mute toggle on top of
     the In-battle button. */
  margin-bottom: 2.2rem;
}

/*
 * Two buttons, one corner, and the ranking between them is the whole point of
 * the styling. Deploy is the accent slab this screen is built around; the
 * tutorial is a quiet ghost button beside it, because it is the thing you press
 * once and the other is the thing you press every session. Bottom-aligned so
 * their baselines agree despite the height difference.
 */
.launch {
  display: flex;
  align-items: flex-end;
  gap: 0.6rem;
}

.tutorial {
  padding: 0.85rem 1.2rem;
  border: 1px solid var(--steel-600);
  border-radius: 4px;
  background: rgba(10, 13, 17, 0.62);
  backdrop-filter: blur(9px);
  color: var(--steel-200, #d7dde3);
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.tutorial:hover {
  border-color: var(--accent);
  color: var(--accent);
}

/* "free" is a fact worth stating on the button, because the one beside it
   charges fuel and the difference is the reason this is replayable. */
.tutorial em {
  font-style: normal;
  font-weight: 500;
  opacity: 0.6;
}

.battle {
  padding: 0.85rem 2.2rem;
  border: 1px solid var(--accent);
  border-radius: 4px;
  background: var(--accent);
  color: #14181d;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  /* It is over a photograph and it is the one thing on the screen that must be
     found without looking, so it gets a shadow the panels do not. */
  box-shadow: 0 0.4rem 1.4rem rgba(0, 0, 0, 0.55);
}

.battle em {
  font-style: normal;
  font-weight: 500;
  opacity: 0.75;
}

/*
 * The bays, at bookmark size rather than card size.
 *
 * A fixed width in `rem` rather than a `1fr` grid, and that is the whole
 * difference: `repeat(3, 1fr)` gave them a third of the window each, so on a
 * wide monitor three saved tanks were rendered larger than they are on the
 * hulls tab that sells them — on the one screen whose entire subject is the
 * tank in the middle. A bay only has to be recognisable and clickable.
 */
.bays {
  display: flex;
  /* Default `align-items: stretch` is what makes this work: every box in the
     row takes the height of the tallest one. Letting that tallest one *be* a
     filled bay (see `.bay.empty` below) is what keeps a filled bay's own
     content flush to its own edges. */
  gap: 0.45rem;
  --bay-lift: 1.07;
}

.bay {
  position: relative;
  width: 5.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.3rem 0.35rem 0.4rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  background: rgba(10, 13, 17, 0.62);
  backdrop-filter: blur(9px);
  color: inherit;
  text-align: left;
}

.bay:hover:not(:disabled) {
  border-color: var(--accent);
}

.bay:disabled {
  opacity: 0.5;
}

/*
 * The tank being driven lifts slightly off the row — a scale on `.bay-pick`,
 * never on `.bay` itself. `.bay`'s own box is what `garage-bays.mjs` measures
 * to prove the strip never reshuffles (`getBoundingClientRect`, which follows
 * a transform), so scaling it would fail that case on every bay switch, not
 * just look wrong. `.bay-pick` has no such promise to keep, and painting
 * outside `.bay`'s own border is exactly the "lifted" look, so `.bay.current`
 * raises its stacking order to sit over the neighbour it now overlaps.
 */
.bay.current {
  z-index: 1;
}

.bay.current .bay-pick {
  transform: scale(var(--bay-lift));
  transition: transform 140ms ease-out;
}

@media (prefers-reduced-motion: reduce) {
  .bay.current .bay-pick {
    transition: none;
  }
}

/*
 * The picture and the name, filling the box. It carries no chrome of its own —
 * the border, the padding and the blur belong to `.bay`, so the clear button can
 * sit inside the same frame rather than beside it.
 */
.bay-pick {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  text-align: left;
}

/*
 * The bay being driven.
 *
 * Marked rather than hidden, and marked by the *frame* rather than by dimming
 * the contents: this is the tank standing in the middle of the screen, so the
 * box should read as "that one" and not as "unavailable". Its button is disabled
 * because clicking it would equip what is already equipped, and `.bay-pick` is
 * exempted from the usual disabled fade for the same reason.
 */
.bay.current {
  border-color: var(--accent);
  box-shadow: inset 0 0 0 1px var(--accent);
}

.bay-pick:disabled {
  opacity: 1;
  cursor: default;
}

.bay:not(.current) .bay-pick:disabled {
  opacity: 0.5;
}

/*
 * The clear button, over the top-right corner of the picture.
 *
 * Absolute so it takes no row — a control that reserved space would make a
 * filled bay taller than an empty one and step the whole strip. Kept faint until
 * the bay is hovered or the button itself is focused, so the corner of every
 * bookmark is not a permanent delete icon; `:focus-visible` is what keeps it
 * reachable without a pointer.
 */
.bay-remove {
  position: absolute;
  top: 0.15rem;
  right: 0.15rem;
  display: grid;
  place-items: center;
  width: 1.15rem;
  height: 1.15rem;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 3px;
  background: rgba(10, 13, 17, 0.78);
  color: var(--steel-400);
  font-size: 0.82rem;
  line-height: 1;
  opacity: 0;
  transition: opacity 120ms ease-out;
}

.bay:hover .bay-remove,
.bay-remove:focus-visible {
  opacity: 1;
}

/* A local literal rather than a palette token, as `ResearchTree.vue` does with
   its amber: the palette has no destructive colour, and inventing one here would
   commit every future red in the app to this shade. */
.bay-remove:hover {
  border-color: #d2544a;
  color: #d2544a;
}

/* No aspect ratio here — the preview brings its own, and two competing ones
   were what pushed the image out of its box. */
.bay-shot {
  display: block;
  overflow: hidden;
  border-radius: 3px;
}

.bay-shot :deep(.preview) {
  width: 100%;
  border: none;
  background: none;
}

.bay-name {
  font-size: 0.68rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bay.empty {
  display: grid;
  place-items: center;
  gap: 0.1rem;
  /*
   * A tap-target floor, not a restated guess at a filled bay's height. It used
   * to be `6.1rem` — "the thumbnail's 4:3 plus its two lines of chrome" — which
   * was taller than a filled bay's real content, and `.bays`' default
   * `align-items: stretch` then stretched every box to match it: the slack
   * landed under `.bay-pick`, which has no `flex-grow` of its own, and showed
   * up as a gap under the name. With the floor this low, the row's tallest box
   * is a filled one and stretch derives the empty boxes' height from it instead.
   */
  min-height: 44px;
  border-style: dashed;
  text-align: center;
}

.plus {
  font-size: 1.3rem;
  line-height: 1;
  color: var(--steel-600);
}

.secondary {
  padding: 0.5rem 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 4px;
  background: rgba(10, 13, 17, 0.62);
  backdrop-filter: blur(9px);
  color: inherit;
  font-size: 0.78rem;
}

.secondary:disabled {
  opacity: 0.45;
}

/*
 * Small desktop: the bays are still card-sized on a window like 1200×719.
 *
 * Same 100rem threshold as `TankPanel.vue`'s own small-desktop tier — the two
 * corners are meant to shrink together. `.bay-shot` is let overflow rather
 * than shrinking the picture again: the preview is already small at 6.6rem,
 * and a second reduction on top of the narrower box would leave a thumbnail
 * too small to tell one hull from another. It is drawn wider than its box
 * instead and centred, spilling past the bay's own edges the way a card's
 * corner badge does — the box gets smaller, the picture does not.
 *
 * **123%, not more, and the gap is left at its default.** The preview is a
 * card in its own right — a border, a rounded corner, a dark gradient fill —
 * not a silhouette on a transparent ground, so overflowing it far enough to
 * reach a neighbour's edge reads as two cards colliding rather than as one
 * picture bleeding tastefully. Measured against the real boxes at this
 * breakpoint (80px bay, 68.4px of that the picture's own content width, a
 * 7.2px gap): 123% clears the bay's own border by ~3px and still leaves
 * ~4px of daylight before the next bay starts, so two neighbouring bays'
 * pictures can never touch however either is populated.
 *
 * `.lobby:not(.compact)` throughout — `.compact` is the phone-landscape tier,
 * driven by height rather than width, and it already has its own bay figures
 * measured against its own gap and padding (see the compact block below). The
 * two ranges can overlap on a short, narrow window, and this tier must not
 * silently reach into a layout it was not measured against.
 */
@media (max-width: 100rem) {
  /*
   * The lift is a large-screen flourish. The picture here is already bled to
   * 123% of the box to stay legible at this size — see `.bay-shot` below — and
   * that bleed is sized against the gap so two neighbouring pictures can never
   * touch. Scaling `.bay-pick` on top of an already-bled picture would break
   * that guarantee, so the lift is switched off rather than re-measured.
   */
  .lobby:not(.compact) .bays {
    --bay-lift: 1;
  }

  .lobby:not(.compact) .bay {
    width: 5rem;
    padding: 0.25rem 0.3rem 0.3rem;
  }

  .lobby:not(.compact) .bay-shot {
    display: flex;
    justify-content: center;
    overflow: visible;
  }

  .lobby:not(.compact) .bay-shot :deep(.preview) {
    flex: none;
    width: 123%;
  }

  .lobby:not(.compact) .bay-pick .bay-name {
    text-align: center;
  }

  .lobby:not(.compact) .bay-name {
    font-size: 0.64rem;
  }

  /* A tap-target floor, same reasoning as the base rule above — stretch derives
     an empty slot's height from a filled neighbour rather than restating one. */
  .lobby:not(.compact) .bay.empty {
    min-height: 44px;
  }
}

/*
 * Narrow enough that the dock's centred cluster would reach the panel's column.
 * See `.floor` for the arithmetic: they meet at 1240 px, and this breaks a little
 * before that. The panel gives up the dock's row rather than being drawn over.
 */
@media (max-width: 78rem) {
  .floor {
    grid-row: 1 / 3;
  }
}

/*
 * One column, stacked: the quest card, then the numbers, then the dock.
 *
 * `.floor` used to take `grid-column: 1 / -1` while staying on row 1, which put
 * it straight on top of whatever held the top-left corner. That was survivable
 * for the readout it overlapped — one transparent line of text — and is not
 * survivable for the card, which is a control. So the two get a row each.
 *
 * The card is **not** hidden here, and that is the difference from the readout
 * this block used to hide (its rule outlived the element by some margin and went
 * with this edit). The readout restated a choice the picker owns; the card is the
 * **only** door to the daily-quest window — `@quests` is what sets `questsOpen`,
 * from nowhere else — so hiding it at 832 px would take the feature away rather
 * than move it. It keeps its own 24 rem inside a stretched track.
 */
@media (max-width: 52rem) {
  .lobby {
    grid-template-columns: minmax(0, 1fr);
  }

  .floor {
    grid-column: 1;
    grid-row: 2;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Compact: the garage floor on a phone held sideways.
 *
 * ── What it looked like before ──────────────────────────────────────────────
 *
 * Measured at 667x375 with `e2e/cases/garage-landscape.mjs`, against a title bar
 * that had already been cut to 54 px:
 *
 *   the content box was **298 px** tall and the grid's fixed rows wanted 360
 *   `.floor` therefore resolved to **638x0** and the tank panel inside it to 384x2
 *   `.stats` tried to scroll **536 px of content in a 19 px box**
 *   `customise-open` sat at y=349 — **below the fold of a 375 px screen**
 *   `.dock` ran to y=389 and the In-battle button's bottom **14 px off-screen**
 *
 * Nothing was wrong with any one rule, and the arithmetic says so exactly. A
 * 375 px screen less a 54 px bar, the padding that clears it and the padding at
 * the foot leaves this grid **297.8 px**; its two fixed rows want a 140 px quest
 * card and a 157 px column of bays-over-a-button, and with 35 px of clearance for
 * the music chip and two 14.4 px gutters that is **360.8 px**. So the `1fr` in the
 * middle — the tank's row, and the only one that can give — went to zero, and then
 * everything below it went past the bottom of the screen.
 *
 * ── What it is instead ─────────────────────────────────────────────────────
 *
 * **The hangar stays full-bleed and the controls go to the four corners.** That
 * is not a new idea here, it is this file's own — *"the quest card, the numbers,
 * the bays and the button are corners of the same picture"* — held to while the
 * middle band shrinks from the tablet's 337 px to 61. What makes it survive the shrink
 * is that the room is a **sibling canvas** `GarageScreen` mounts behind every
 * tab: the grid decides where the glass sits, never how much tank is drawn, so a
 * thin middle row veils the vehicle rather than deleting it, and the vehicle is
 * still the biggest thing on screen and still the biggest drag target.
 *
 * Corners, and what each one gave up to fit:
 *
 *   top left     the daily-quest card, at 15.5 rem instead of 24
 *   top right    the tank's name and one Customize button — no rank, no stats
 *   bottom left  the four bays, at bookmark-of-a-bookmark size
 *   bottom right the way out, which gave up nothing
 *
 * ── The three decisions worth arguing with ─────────────────────────────────
 *
 * **The dock is a row, not a column.** On the desktop floor the button sits
 * *under* the bays because it is the conclusion of them, and that is still the
 * better reading — it is simply 157 px tall, which is half of everything there
 * is. Side by side it is 64. What buys the arrangement is that a landscape phone
 * is the one shape with width to spare: 667 px of it against 375 of height. The
 * consequence to keep in mind is that this row **may hold exactly two children**
 * for the same reason the centred column may (see `.dock`): a confirmation goes
 * through the shared banner rather than a strip in this row, and a third
 * control belongs inside `.bays` or beside the button rather than between them.
 *
 * **The card is narrowed and not shortened.** 24 rem is 58% of a 667 px screen,
 * which is what put a panel over the tank's own shoulder. Every row inside it
 * either ellipsizes (`.name`) or is `flex: none` (the reward, the clock), so
 * taking 136 px of width off it costs **no height at all** — verified, the card
 * measures the same 140 px at both widths. It is done by capping `.quests`
 * rather than by reaching into the card with `:deep()`, because the card already
 * delegates exactly this to its container: its own `max-width: 100%` beside a
 * fixed `width` is the escape hatch, and using it means the placement stays the
 * corner's decision and the card's internals stay the card's.
 *
 * **The music chip used to cost a margin, and it is gone.** The hope was that
 * bays-left plus button-right would leave the bottom centre free and the 2.2 rem
 * could go — `MusicControl` pinned a chip at `left: 50%; bottom: 0.85rem`,
 * measured 106x27, and at 667 the bays' right edge is x=326 against a chip
 * spanning 281–387, so it landed on the last bay's picture. The margin came to
 * 2 rem rather than 2.2 to clear it by 6 px, and the saving was the row rather
 * than the margin.
 *
 * The chip is **no longer drawn anywhere** (owner instruction, 2026-09-02; the
 * defect that preceded it was a 975 px² overlap with the hangar's own *Drag to
 * look around* hint, which is pinned bottom-centre too, and the reason it could
 * simply go is that `SettingsPanel`'s Audio group already carries the control and
 * states the `M` key). So nothing on the bottom centre constrains this row any
 * more — but the bottom *left* does, and harder: the chat dock's toggle is a
 * 44 px bar in that corner now. The margin is 3 rem for that, measured below.
 * ═══════════════════════════════════════════════════════════════════════════ */

/*
 * Two columns at every width, overriding both media queries above.
 *
 * The 52 rem collapse to one column exists because a 24 rem panel beside a card
 * does not fit a narrow window — and the panel is 13 rem here and carries no
 * stats, so the premise is gone. Stacked, the card and the plate would cost two
 * rows of a screen that has room for one. Specificity does the overriding rather
 * than source order, so these cannot be undone by a query moving.
 */
.lobby.compact {
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.55rem;
}

/*
 * The card, narrowed by its container. See the block above for why here and not
 * inside the card, and why it costs no height.
 */
.lobby.compact .quests {
  max-width: 15.5rem;
}

/*
 * The re-roll button in the narrowed card. Icon and price only — the word took
 * the row the count and the standing share and pushed it onto a second line —
 * and a hit area grown to the 44px floor around a button that stays its size,
 * so the card does not get a row taller to make room for a thumb.
 */
.lobby.compact .quests :deep(.tally) {
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.5rem;
}

.lobby.compact .quests :deep(.reroll) {
  margin-left: 0.4rem;
  padding: 0.25rem 0.3rem 0.25rem 0.4rem;
  white-space: nowrap;
}

.lobby.compact .quests :deep(.reroll .word) {
  display: none;
}

.lobby.compact .quests :deep(.reroll)::after {
  content: '';
  position: absolute;
  inset: 50% auto auto 50%;
  width: max(100%, 44px);
  height: 44px;
  transform: translate(-50%, -50%);
}

.lobby.compact .quests :deep(.standing) {
  white-space: nowrap;
}

/*
 * The name plate: its own corner, its own height, and no share of the dock's row.
 *
 * `align-self` is left at the grid's `start` rather than `stretch` — the opposite
 * of the desktop rule, and for the opposite reason. There, stretching is what
 * gives the stat list a height to scroll inside; here there is no list, so a
 * stretched plate would be 200 px of empty glass over the tank's flank.
 */
.lobby.compact .floor {
  grid-column: 2;
  grid-row: 1;
  align-self: start;
}

/* The refusal, sized to the plate it sits over rather than to a 22 rem desktop
   column — otherwise one long sentence widens this whole corner and pushes the
   plate across the tank. */
.lobby.compact .battle-blocked {
  max-width: 13rem;
  padding: 0.35rem 0.5rem;
  font-size: 0.66rem;
}

/*
 * Bays left, the way out right, both sitting on the same baseline.
 *
 * **This row's bottom edge is a number another component depends on**, and it
 * used to be the top edge. `ChatDock` was lifted to `bottom: 7.5rem` to stand
 * clear of the row's *top*; that put its collapsed bar in the middle of the
 * screen, so the toggle is back in the bottom-left corner and the panel grows
 * upward out of it (owner instruction, 2026-09-02 — the panel may cover this
 * row, the toggle may not). What has to hold is therefore the other edge, and
 * only this file can move a row in this grid.
 *
 * The margin is 3rem, and both halves are bottom-anchored so the clearance does
 * not move with the viewport. Measured: at 2rem the bay row's bottom edge sat
 * **46.4px** above the viewport bottom against a toggle whose top edge is at
 * 53px (`bottom: 0.5rem`, a 44px tap floor and the dock's border) — 6.6px of
 * overlap on the bays' own bottom band. 3rem puts the row's bottom at 62.4px and
 * clears the toggle by **9.4px**, identically at 667x375, 844x390 and 915x412,
 * with `elementFromPoint` answering with the bay control itself at the centre of
 * all four.
 *
 * If the bay size, this margin, or the dock's `bottom`/height changes,
 * re-measure and move the other with it; neither component can see the other.
 */
.lobby.compact .dock {
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.6rem;
  /* Clears the chat toggle. See the measurement above. */
  margin-bottom: 3rem;
}

.lobby.compact .bays {
  gap: 0.35rem;
}

/*
 * A bookmark of a bookmark: 4.6 rem instead of 6.6.
 *
 * Four of them plus their gutters is 311 px, which is what is left of a 667 px
 * row once the way out has taken 185 and the gutters 12. The name stays — a
 * picture at this size says *a tank*, and the word is what says *which* — and it
 * is the `.bay-pick` button underneath that has to clear 44 px, which it does at
 * a measured 63x54 because the thumbnail's own 8:5 gives it the height.
 */
.lobby.compact .bays {
  --bay-lift: 1;
}

.lobby.compact .bay {
  width: 4.6rem;
  padding: 0.25rem 0.28rem 0.3rem;
}

.lobby.compact .bay-name {
  font-size: 0.62rem;
}

/*
 * An empty bay is 44 px on its own and otherwise as tall as its neighbours.
 *
 * `.bays` is a flex row at the default `align-items: stretch`, so a filled bay's
 * height is already every bay's height and the 6.1 rem the desktop states — the
 * thumbnail plus its two lines of chrome — is a *floor* rather than the mechanism.
 * At compact size that 98 px floor is 33 px taller than the 65 px box it is meant
 * to be matching, so it would step the strip in the other direction. It becomes
 * the tap-target floor instead, which is the only height this button needs of its
 * own: with a tank parked, stretch makes it 65 px like its neighbours, and with
 * the whole strip empty — reachable by clearing every bay on a desktop — four free
 * bays are 44 px and still pressable.
 */
.lobby.compact .bay.empty {
  min-height: 44px;
}

.lobby.compact .plus {
  font-size: 1.05rem;
}

/*
 * The way out, and it is the one corner that gives up nothing it needs.
 *
 * Smaller type and tighter padding — 241 px to about 185 — because the width it
 * was spending is the width the bays needed, and it is still the only accent slab
 * on the tab and still the widest single control on the row. `min-height: 44px`
 * is belt to the padding's braces.
 */
.lobby.compact .battle {
  min-height: 44px;
  padding: 0.7rem 1.1rem;
  font-size: 0.92rem;
}
</style>
